import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BASE_URL } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
// const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TAGS_PER_PAGE = 9;

const Tags = () => {
  const [tags, setTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortType, setSortType] = useState('popular');
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTags = async () => {
      setLoading(true);
      setError('');
      try {
        // const response = await fetch('http://localhost:8000/api/tags/',{
        const response = await fetch(`${BASE_URL}/tags/`, {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch tags: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        setTags(data);
      } catch (err) {
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, []);

  // Filter tags by search term (case-insensitive)
  const filteredTags = tags.filter(tag =>
    tag.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort filtered tags by popularity or name
  const sortedTags = filteredTags.sort((a, b) => {
    if (sortType === 'popular') {
      return (b.questions_count || 0) - (a.questions_count || 0);
    }
    return a.name.localeCompare(b.name);
  });

  const totalPages = Math.ceil(sortedTags.length / TAGS_PER_PAGE);
  const paginatedTags = sortedTags.slice(
    (currentPage - 1) * TAGS_PER_PAGE,
    currentPage * TAGS_PER_PAGE
  );

  const handlePageChange = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  };

  // Reset to first page when searchTerm or sortType changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortType]);

  if (loading) return (
    <div className="container mx-auto py-6">
      <p className="text-muted-foreground">Loading tags...</p>
    </div>
  );
  
  if (error) return (
    <div className="container mx-auto py-6">
      <p className="text-destructive">Error: {error}</p>
    </div>
  );

  return (
    <div className="container mx-auto py-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Tags</CardTitle>
          <p className="text-muted-foreground">
        A tag categorizes your question with other similar questions. Use relevant tags to help others find your question.
      </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Input
          placeholder="Search tags"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
        />

            <Tabs value={sortType} onValueChange={setSortType} className="w-full sm:w-auto">
              <TabsList>
                <TabsTrigger value="popular">Popular</TabsTrigger>
                <TabsTrigger value="name">Name</TabsTrigger>
              </TabsList>
            </Tabs>
      </div>

      {paginatedTags.length === 0 ? (
            <p className="text-muted-foreground">No tags found.</p>
      ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedTags.map((tag) => (
                <Link 
                  to={`/dashboard/tags/${encodeURIComponent(tag.name)}`} 
                  key={tag.id}
                >
                  <Card className="h-full hover:bg-accent/50 transition-colors">
                    <CardContent className="pt-6">
                      <div className="font-semibold text-lg">#{tag.name}</div>
              {tag.description && (
                        <p className="text-sm text-muted-foreground mt-2">
                          {tag.description.length > 100 
                            ? tag.description.slice(0, 100) + '...' 
                            : tag.description}
                        </p>
              )}
                      <p className="text-sm text-muted-foreground mt-4">
                        {tag.questions_count || 0} questions
                      </p>
                    </CardContent>
                  </Card>
            </Link>
          ))}
        </div>
      )}

      {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
          {[...Array(totalPages)].map((_, index) => (
                <Button
              key={index + 1}
                  variant={currentPage === index + 1 ? "default" : "outline"}
              onClick={() => handlePageChange(index + 1)}
            >
              {index + 1}
                </Button>
          ))}
              <Button
                variant="outline"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
            Next
              </Button>
        </div>
      )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Tags;
